import { useMemo } from "react";

import type { ChartTableSelection } from "../../lib/chartTables.js";
import { SHARED_FULL_WEEKS_CRITERIA, SHARED_TABLES_CRITERIA } from "../../lib/chartTables.js";
import { selectedDayUptime } from "../../lib/chartTableSeries.js";
import {
  dailyChartBarAxisProps,
  dailyChartRotateAxisLabels,
  formatDashboardDayLabelLong,
} from "../../lib/dashboardDayLabel.js";
import { formatGbpPence } from "../../lib/formatMoney.js";
import {
  formatWeekAxisLabel,
  FULL_WEEKS_EMPTY_MESSAGE,
  groupFullMondayWeeks,
  shiftReportingDayKey,
  type UptimeWeekBucket,
} from "../../lib/uptimeWeekBuckets.js";
import { useSharedChartTables } from "../../lib/useSharedChartTables.js";
import type { FilterState, RetentionData } from "../../types.js";
import { ChartCard } from "../ChartCard.js";
import type { BarChartItem } from "../charts/BarChart.js";
import { BarChart } from "../charts/BarChart.js";
import { ChartTablePicker } from "./ChartTablePicker.js";

/** Same green as Revenue (by day) / Gross Rev. */
const REVENUE_FILL = "#31c48d";

const VALUE_LABEL_MAX_BARS = 7;

type RevenueDay = RetentionData["revenueByDay"][number];

function formatGbpAxisTick(pounds: number): string {
  if (pounds >= 1000) return `£${Math.round(pounds).toLocaleString("en-GB")}`;
  if (pounds >= 10) return `£${Math.round(pounds)}`;
  if (pounds > 0 && pounds < 1) return `£${pounds.toFixed(2)}`;
  return `£${(Math.round(pounds * 10) / 10).toFixed(pounds % 1 === 0 ? 0 : 1)}`;
}

function perTablePounds(revenuePence: number, tableCount: number): number | null {
  return tableCount > 0 ? revenuePence / 100 / tableCount : null;
}

function weekRevenuePence(
  week: UptimeWeekBucket,
  byDay: ReadonlyMap<string, RevenueDay>,
): number {
  let revenuePence = 0;
  for (let i = 0; i < 7; i++) {
    const day = shiftReportingDayKey(week.monday, i);
    revenuePence += (day && byDay.get(day)?.revenuePence) || 0;
  }
  return revenuePence;
}

const CRITERIA = `Bar = **effective payment_confirmed £ ÷ number of tables checked** in the shared Tables dropdown (omit = all named tables in venue/device scope).

Same revenue grain as **Revenue** / Gross Rev. (date + venue + device; **no Game**). With 8 tables selected, each day’s bar is that day’s total revenue ÷ 8 — not revenue per uptime hour.

**Full weeks** (checkbox, off by default; shared URL \`weeks=1\`): one bar per complete **Monday–Sunday** whose seven days are inside the window and each have a full day's capacity (same rule as Revenue). Value is **summed week revenue ÷ table count** (same N for the week), not an average of daily per-table figures. Partial weeks are omitted.

Value labels above bars are omitted when there are more than 7 days or weeks. No Raw Events drill-down.

${SHARED_TABLES_CRITERIA}

${SHARED_FULL_WEEKS_CRITERIA}`;

export function RevenuePerTableByDayCard({
  rows: fleetRows,
  tableCount: fleetTableCount,
  devices,
  uptimeByDeviceDay,
  revenueByDeviceDay,
  chartTables,
  onChartTablesChange,
  fullWeeks,
  onFullWeeksChange,
  filters,
}: {
  rows: RevenueDay[];
  tableCount: number;
  devices: RetentionData["chartDevices"];
  uptimeByDeviceDay: RetentionData["uptimeByDeviceDay"];
  revenueByDeviceDay: RetentionData["revenueByDeviceDay"];
  chartTables: ChartTableSelection;
  onChartTablesChange: (next: ChartTableSelection) => void;
  fullWeeks: boolean;
  onFullWeeksChange: (next: boolean) => void;
  filters: FilterState;
}) {
  const tables = useSharedChartTables(devices, chartTables, onChartTablesChange, filters);
  const rows = useMemo(() => {
    if (tables.useFleetSeries) return fleetRows;
    if (tables.effectiveIds.size === 0) return [];
    return fleetRows.map((row) => {
      const up = selectedDayUptime(uptimeByDeviceDay, tables.effectiveIds, row.day);
      let revenuePence = 0;
      for (const payment of revenueByDeviceDay) {
        if (payment.day === row.day && tables.effectiveIds.has(payment.deviceId)) {
          revenuePence += payment.revenuePence;
        }
      }
      return {
        day: row.day,
        revenuePence,
        uptimeHours: up.uptimeHours,
        revenuePerUptimeHour: null,
        capacityHours: up.capacityHours,
        unmeasuredHours: up.unmeasuredHours,
      };
    });
  }, [
    fleetRows,
    revenueByDeviceDay,
    tables.effectiveIds,
    tables.useFleetSeries,
    uptimeByDeviceDay,
  ]);
  const tableCount = tables.useFleetSeries ? fleetTableCount : tables.effectiveIds.size;
  const byDay = useMemo(() => new Map(rows.map((row) => [row.day, row])), [rows]);

  const weeks = useMemo(() => {
    if (!fullWeeks || tableCount <= 0) return [];
    return groupFullMondayWeeks(
      rows.map((row) => ({
        day: row.day,
        uptimeHours: row.uptimeHours,
        scheduledHours: 0,
        capacityHours: row.capacityHours,
        unmeasuredHours: row.unmeasuredHours,
        expectedHours: 0,
      })),
      tableCount,
    );
  }, [fullWeeks, rows, tableCount]);

  const bars: BarChartItem[] = useMemo(() => {
    if (fullWeeks) {
      return weeks.map((week) => {
        const revenuePence = weekRevenuePence(week, byDay);
        const perTable = perTablePounds(revenuePence, tableCount);
        return {
          label: formatWeekAxisLabel(week.monday, week.sunday),
          value: perTable ?? 0,
          fill: REVENUE_FILL,
          valueLabel: perTable == null ? "—" : formatGbpPence(Math.round(perTable * 100)),
        };
      });
    }
    return rows.map((row) => {
      const perTable = perTablePounds(row.revenuePence, tableCount);
      return {
        ...dailyChartBarAxisProps(row.day),
        value: perTable ?? 0,
        fill: REVENUE_FILL,
        valueLabel: perTable == null ? "—" : formatGbpPence(Math.round(perTable * 100)),
      };
    });
  }, [byDay, fullWeeks, rows, tableCount, weeks]);

  const shownRevenuePence = fullWeeks
    ? weeks.reduce((sum, week) => sum + weekRevenuePence(week, byDay), 0)
    : rows.reduce((sum, row) => sum + row.revenuePence, 0);
  const overallPerTable = perTablePounds(shownRevenuePence, tableCount);
  const showValueLabels = bars.length > 0 && bars.length <= VALUE_LABEL_MAX_BARS;

  return (
    <ChartCard
      compact
      title="Revenue per table"
      titleRight={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-[12px] font-medium text-text-primary">
            <input
              type="checkbox"
              className="accent-accent-2"
              checked={fullWeeks}
              onChange={(e) => onFullWeeksChange(e.target.checked)}
            />
            Full weeks
          </label>
          <span className="shrink-0 text-[12px] font-medium tabular-nums text-text-secondary">
            {tableCount.toLocaleString()} table{tableCount === 1 ? "" : "s"}
            {overallPerTable != null
              ? ` · ${formatGbpPence(Math.round(overallPerTable * 100))} / table`
              : ""}
          </span>
        </div>
      }
      criteria={CRITERIA}
      note={
        fullWeeks
          ? "One bar per complete Monday–Sunday. Value is week revenue ÷ selected tables. Partial weeks (including this week if it has not finished) are omitted."
          : "Divides each day's take by how many tables are checked — compare fleets of different sizes."
      }
    >
      <div className="mb-1 flex justify-end">
        <ChartTablePicker
          devices={devices}
          selectedIds={tables.effectiveIds}
          onSelectedIdsChange={tables.onSelectedIdsChange}
          locked={tables.filterLocksSingle}
        />
      </div>
      {!tables.useFleetSeries && tables.effectiveIds.size === 0 ? (
        <p className="py-5 text-[13px] text-text-secondary">Select at least one table.</p>
      ) : fullWeeks && bars.length === 0 ? (
        <p className="py-5 text-[13px] text-text-secondary">
          {FULL_WEEKS_EMPTY_MESSAGE}
        </p>
      ) : (
        <BarChart
          data={bars}
          fillBars
          fillWidth
          compact={dailyChartRotateAxisLabels(bars.length)}
          verbatimAxisLabels
          rotateAxisLabels={dailyChartRotateAxisLabels(bars.length)}
          axisLabelMaxChars={fullWeeks ? 12 : 10}
          showYAxis
          showValueLabels={showValueLabels}
          formatYAxisTick={formatGbpAxisTick}
          barTooltip={(item, index) => {
            if (fullWeeks) {
              const week = weeks[index];
              if (!week) return item.label;
              const revenuePence = weekRevenuePence(week, byDay);
              const perTable = perTablePounds(revenuePence, tableCount);
              const perBit =
                perTable == null ? "—" : formatGbpPence(Math.round(perTable * 100));
              return `${formatDashboardDayLabelLong(week.monday)} – ${formatDashboardDayLabelLong(week.sunday)}: ${formatGbpPence(revenuePence)} ÷ ${tableCount} table${tableCount === 1 ? "" : "s"} = ${perBit} / table. No Raw Events drill-down.`;
            }
            const row = rows[index];
            if (!row) return item.label;
            const perTable = perTablePounds(row.revenuePence, tableCount);
            const perBit =
              perTable == null ? "—" : formatGbpPence(Math.round(perTable * 100));
            return `${formatDashboardDayLabelLong(row.day)}: ${formatGbpPence(row.revenuePence)} ÷ ${tableCount} table${tableCount === 1 ? "" : "s"} = ${perBit} / table. No Raw Events drill-down.`;
          }}
        />
      )}
    </ChartCard>
  );
}
